/**
 * The per-platform builds of a version row (D1 `publish_versions.native_targets`,
 * private/auth migration 0080 — internal/publish types.ts NativeTargetRecord[])
 * and the macOS (darwin-arm64) state the Registry page shows for it.
 *
 * darwin-arm64 is DECOUPLED from linux (internal/publish
 * steps/darwinFollowUp.ts, user decision 2026-10-13): linux publishes first;
 * a follow-up then builds darwin when a runner is free and publishes a new
 * build that adds it. Its record moves through
 *   pending (waiting for / building on a runner, or publishing — `reason`)
 *   → shipped (in build `publishedIn`)        → "macOS: published"
 *   → failed (`reason`: compile error, timed out, abandoned in the queue,
 *             superseded by a newer build)     → "macOS: failed: <reason>"
 * A `disabled` record (DARWIN_ADDON_ENABLED off) or none at all shows nothing.
 */

/** Mirrors internal/publish types.ts NativeTargetRecord (parsed
 *  defensively — the broker stores it verbatim). */
export interface NativeTargetRecord {
  platform: string;
  triple?: string | null;
  package?: string | null;
  status: string;
  reason?: string;
  jobId?: string;
  hardwareVerified?: boolean;
  binaryBytes?: number | null;
  publishedIn?: string;
  /** The macOS benchmark outcome (internal/publish types.ts
   *  NativeTargetRecord.macBench, bench/macos.ts macosBenchBadge). */
  macBench?: {
    state?: string;
    reason?: string;
    measuredAt?: number;
  };
  /** What the macOS verification host said about the darwin addon
   *  (internal/publish types.ts MacVerifyRecord, steps/macVerify.ts). */
  macVerify?: {
    status?: string;
    reason?: string;
    jobId?: string;
    checkedAt?: number;
    verifiedBy?: { platform?: string; chip?: string; os?: string };
    gating?: string;
    gated?: boolean;
    durationMs?: number;
  };
}

/** `native_targets` JSON → records; [] when unset or unreadable. */
export function parseNativeTargets(
  raw: string | null | undefined,
): NativeTargetRecord[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    return v.filter(
      (t): t is NativeTargetRecord =>
        !!t &&
        typeof t === "object" &&
        typeof (t as { platform?: unknown }).platform === "string" &&
        typeof (t as { status?: unknown }).status === "string",
    );
  } catch {
    return [];
  }
}

/** Version statuses in which this attempt has not published yet. A darwin
 *  record on the row in these statuses was written by an EARLIER attempt (the
 *  publisher drops it when an attempt starts, and writes the attempt's own
 *  after its publish), so it must never be shown as this attempt's: its job,
 *  telemetry and "shipped in <build>" belong to the previous run. */
export const PRE_PUBLISH_STATUSES: ReadonlySet<string> = new Set([
  "pending",
  "compiling_package_test",
  "testing_package_test",
  "compiling_full_project",
  "testing_full_project",
  "compiling_addon",
  "verifying_addon",
]);

/** The darwin-arm64 record, split into the one that belongs to the attempt
 *  with this status and the one that is a previous attempt's. */
export function darwinRecordFor(
  raw: string | null | undefined,
  attemptStatus?: string | null,
): { current: NativeTargetRecord | null; previous: NativeTargetRecord | null } {
  const t =
    parseNativeTargets(raw).find((x) => x.platform === "darwin-arm64") ?? null;
  if (t && attemptStatus && PRE_PUBLISH_STATUSES.has(attemptStatus))
    return { current: null, previous: t };
  return { current: t, previous: null };
}

export type MacosState =
  | { kind: "pending"; label: string; detail: string }
  | { kind: "published"; label: string; detail: string }
  | { kind: "failed"; label: string; reason: string };

/** Longest reason shown inline (the full text is in the tooltip). */
export const MACOS_REASON_INLINE_MAX = 80;

function inlineReason(reason: string): string {
  const first = reason.split("\n", 1)[0]!.trim();
  return first.length > MACOS_REASON_INLINE_MAX
    ? `${first.slice(0, MACOS_REASON_INLINE_MAX - 1)}…`
    : first;
}

/** The macOS (darwin-arm64) state of a version row, or null when there is
 *  nothing to show (no darwin record, or darwin disabled). */
export function macosState(raw: string | null | undefined): MacosState | null {
  const t = parseNativeTargets(raw).find((r) =>
    r.platform.startsWith("darwin-"),
  );
  if (!t) return null;
  switch (t.status) {
    case "pending":
      return {
        kind: "pending",
        label: "macOS: pending",
        detail:
          t.reason ??
          "the darwin-arm64 build follows the published linux build",
      };
    case "shipped": {
      const where = `${t.publishedIn ? `in build ${t.publishedIn}` : "in this build"}${t.package ? ` (${t.package})` : ""}`;
      const v = macosVerifyState(raw);
      return {
        kind: "published",
        label: "macOS: published",
        detail:
          v?.kind === "verified"
            ? `${where}; ${v.detail}`
            : `${where}; unverified on hardware (static checks only)`,
      };
    }
    case "failed": {
      const reason = t.reason ?? "failed";
      return {
        kind: "failed",
        label: `macOS: failed: ${inlineReason(reason)}`,
        reason,
      };
    }
    default:
      return null;
  }
}

/**
 * The macOS HARDWARE VERIFICATION state of a version row (the badge next to
 * the build badge; plans/2026-10-15-macos-fleet-host.md 6.6). From
 * `macVerify` on the darwin record (internal/publish steps/macVerify.ts):
 *
 *   verified    the addon loaded natively and printed node's output on a real
 *               Mac (the record carries the chip / macOS it ran on)
 *   unverified  nothing proved it on hardware: the Mac was offline, drained or
 *               degraded, or the follow-up never reached it. Never blocks.
 *   failed      the Mac WAS available and the addon did not load or its
 *               output differs; `gated` says the darwin addon was not shipped
 *
 * Null when there is no darwin record, or the darwin build itself is not
 * shipped and nothing was verified (pending / failed builds have no verdict).
 */
export type MacosVerifyState =
  | { kind: "verified"; label: string; detail: string }
  | { kind: "unverified"; label: string; detail: string }
  | { kind: "failed"; label: string; detail: string };

export function macosVerifyState(
  raw: string | null | undefined,
): MacosVerifyState | null {
  const t = parseNativeTargets(raw).find((r) =>
    r.platform.startsWith("darwin-"),
  );
  if (!t) return null;
  const mv = t.macVerify;
  if (!mv || typeof mv.status !== "string") {
    if (t.status !== "shipped") return null;
    return {
      kind: "unverified",
      label: "macOS: unverified",
      detail:
        "Not run on a Mac: no verification verdict was recorded (static checks only).",
    };
  }
  const why = mv.reason ? inlineReason(mv.reason) : "";
  switch (mv.status) {
    case "ok": {
      const by = mv.verifiedBy;
      const hw = [by?.chip, by?.os ? `macOS ${by.os}` : undefined]
        .filter((x): x is string => !!x)
        .join(" · ");
      return {
        kind: "verified",
        label: "macOS: verified",
        detail: `verified on hardware${hw ? ` (${hw})` : ""}: the addon loaded natively and its output matched node${mv.jobId ? `; job ${mv.jobId}` : ""}`,
      };
    }
    case "failed":
      return {
        kind: "failed",
        label: `macOS: verify failed${why ? `: ${why}` : ""}`,
        detail: `${mv.reason ?? "the addon failed on the Mac"}. ${
          mv.gated
            ? "The darwin addon was NOT shipped because of it (linux stays live)."
            : "Flagged only (report mode): the darwin addon shipped anyway."
        }`,
      };
    case "unavailable":
      return {
        kind: "unverified",
        label: "macOS: unverified",
        detail: `No Mac verified it${mv.reason ? `: ${mv.reason}` : ""}. The publish was not affected.`,
      };
    default:
      return {
        kind: "unverified",
        label: "macOS: unverified",
        detail: `Not verified on hardware${mv.reason ? `: ${mv.reason}` : ""}.`,
      };
  }
}

/**
 * The macOS BENCHMARK state of a version row (a second badge next to the
 * build badge, from `macBench` on the darwin record; internal/publish
 * bench/macos.ts):
 *
 *   benchmarked  numbers merged into the registry stats page (macOS arm64 tab)
 *   noisy        merged with a visible badge: wall times varied (thermals,
 *                discarded rounds, wide intervals); read ratios/counters
 *   invalid      the Mac was not in a stable state during the run; no figures
 *                are published
 *   unavailable  no Mac produced numbers (offline, queue expired, ...)
 *
 * Null when the row has no darwin record or no benchmark was attempted.
 */
export type MacosBenchState =
  | { kind: "benchmarked"; label: string; detail: string }
  | { kind: "noisy"; label: string; detail: string }
  | { kind: "invalid"; label: string; detail: string }
  | { kind: "unavailable"; label: string; detail: string };

export function macosBenchState(
  raw: string | null | undefined,
): MacosBenchState | null {
  const t = parseNativeTargets(raw).find((r) =>
    r.platform.startsWith("darwin-"),
  );
  const b = t?.macBench;
  if (!b || typeof b.state !== "string") return null;
  const why = b.reason ? inlineReason(b.reason) : "";
  switch (b.state) {
    case "ok":
      return {
        kind: "benchmarked",
        label: "macOS: benchmarked",
        detail:
          "Benchmarked on the Mac (node 26/24/22 + bun); shown on the registry stats page, macOS arm64 tab.",
      };
    case "noisy":
      return {
        kind: "noisy",
        label: "macOS: noisy",
        detail: `Benchmarked, but the measurement was noisy${why ? ` (${why})` : ""}; the stats page shows a noisy badge. Ratios and instruction counts are still usable.`,
      };
    case "invalid":
      return {
        kind: "invalid",
        label: "macOS: bench invalid",
        detail: `The Mac was not in a stable state during the run, so no macOS figures were published${b.reason ? `: ${b.reason}` : ""}.`,
      };
    case "unavailable":
      return {
        kind: "unavailable",
        label: `macOS bench: unavailable${why ? `: ${why}` : ""}`,
        detail: `${b.reason ?? "No Mac produced numbers"}. The publish was not affected.`,
      };
    default:
      return null;
  }
}
